import { useRef, useCallback, useMemo, useEffect } from 'react';
import analytics, { hashId } from '../utils/analytics';

/**
 * useGameBase - small reusable hook for game components.
 * Provides tolerant player lookup, turn detection, focus ref and submitAction helper.
 */
export default function useGameBase({ gameState, gamePlayers = [], user = {}, socket = null, roomId = null, perspectivePlayer = null }) {
  const focusRef = useRef(null);

  const perspectiveUser = useMemo(() => {
    if (!perspectivePlayer) return user || {};
    return {
      ...(user || {}),
      socketId: perspectivePlayer.socketId || user?.socketId || null,
      userId: perspectivePlayer.userId || user?.userId || null,
      id: perspectivePlayer.id || user?.id || null,
      username: perspectivePlayer.username || user?.username || null,
      color: perspectivePlayer.color || user?.color,
      icon: perspectivePlayer.icon || user?.icon,
      flag: typeof perspectivePlayer.flag !== 'undefined' ? perspectivePlayer.flag : user?.flag,
      viewedAsSpectator: true,
      actualSocketId: user?.socketId || null,
      actualUserId: user?.userId || null,
      actualUsername: user?.username || null,
    };
  }, [perspectivePlayer, user]);

  const perspectiveIds = useMemo(() => {
    const ordered = [
      perspectiveUser?.socketId,
      perspectiveUser?.userId,
      perspectiveUser?.id,
      perspectiveUser?.username,
    ].filter(Boolean);
    return Array.from(new Set(ordered));
  }, [perspectiveUser]);

//   console.log(gamePlayers[0].socketId + " " + gamePlayers[0].userId);
//   console.log(gamePlayers[1].socketId + " " + gamePlayers[1].userId);
//   console.log(gameState.currentPlayerSocketId, gameState.scores);

  const findPlayerById = useCallback((id) => {
    if (!id || !Array.isArray(gamePlayers)) return null;
    let p = gamePlayers.find((pl) => pl.socketId === id || pl.id === id);
    if (p) return p;
    p = gamePlayers.find((pl) => pl.userId === id);
    if (p) return p;
    p = gamePlayers.find((pl) => pl.username === id);
    return p || null;
  }, [gamePlayers]);

  const getCurrentPlayer = useCallback(() => {
    const current = gameState && (gameState.currentPlayerSocketId || gameState.currentPlayer || null);
    if (!current) return null;
    return findPlayerById(current) || null;
  }, [gameState, findPlayerById]);

  const isLocalPlayerTurn = useCallback(() => {
    const current = gameState && (gameState.currentPlayerSocketId || gameState.currentPlayer || null);
    if (!current) return false;
    if (perspectiveUser && perspectiveUser.socketId && perspectiveUser.socketId === current) return true;
    if (perspectiveUser && perspectiveUser.userId && perspectiveUser.userId === current) return true;
    const cp = findPlayerById(current);
    if (cp && perspectiveUser && perspectiveUser.userId && cp.userId === perspectiveUser.userId) return true;
    return false;
  }, [gameState, perspectiveUser, findPlayerById]);

  const normalizedState = useMemo(() => {
    const s = gameState || {};
    const clone = { ...s };
    if (!clone.scores || typeof clone.scores !== 'object') {
      clone.scores = {};
      if (Array.isArray(clone.players)) {
        for (const p of clone.players) {
          clone.scores[p.userId || p.socketId || p.id] = typeof p.score !== 'undefined' ? p.score : 0;
        }
      }
    }
    return clone;
  }, [gameState]);

  // // Centralized analytics: record when a play session starts/ends for any game component
  // const playStartRef = useRef(null);
  // useEffect(() => {
  //   try {
  //     if (!gameState) return;
  //     const started = !!(gameState.started || gameState.phase === 'playing');
  //     const finished = !!(gameState.finished || gameState.phase === 'finished');
  //     const playerCount = Array.isArray(gamePlayers) ? gamePlayers.length : (gameState && Array.isArray(gameState.players) ? gameState.players.length : 0);
  //     const gameName = gameState.gameType || (gameState.config && gameState.config.gameClass) || (gameState.gameClass || null) || null;
  //     const roomIdOrGlobal = roomId || (gameState && gameState.roomId) || (typeof window !== 'undefined' && window.__ROOM_ID) || null;
  //     const roomHash = roomIdOrGlobal ? hashId(roomIdOrGlobal) : null;

      
  //     if (started && !playStartRef.current) {
  //       playStartRef.current = Date.now();
  //       // GA event: record game play start. Use a single event name `game_play` with action 'start'
  //       // analytics.event('game_start', {
  //       //   //action: "Started " + gameName,
  //       //   //category: 'game',
  //       //   game_name: gameName || 'unknown',
  //       //   //room_hash: roomHash,
  //       //   player_count: playerCount,
  //       //   //mode: (gameState && (gameState.mode || (gameState.config && gameState.config.mode))) || null,
  //       // });
  //     }

  //     if (finished && playStartRef.current) {
        
  //       // const dur = Date.now() - playStartRef.current;
  //       // // GA event: record game play end with duration
  //       // analytics.event('game_end', {
  //       //   //action: "Ended " + gameName,
  //       //   //category: 'game',
  //       //   game_name: gameName || 'unknown',
  //       //   //room_hash: roomHash,
  //       //   player_count: playerCount,
  //       //   duration_seconds: dur / 1000,
  //       // });
  //       // playStartRef.current = null;
  //     }
  //   } catch (e) {
  //     // swallow analytics errors
  //   }
  //   return () => {
  //     try {
  //       if (playStartRef.current) {
  //         const dur = Date.now() - playStartRef.current;
  //         const roomIdOrGlobal = roomId || (gameState && gameState.roomId) || (typeof window !== 'undefined' && window.__ROOM_ID) || null;
  //         const roomHash = roomIdOrGlobal ? hashId(roomIdOrGlobal) : null;
  //         const playerCount = Array.isArray(gamePlayers) ? gamePlayers.length : (gameState && Array.isArray(gameState.players) ? gameState.players.length : 0);
  //         const gameName = (gameState && (gameState.gameType || (gameState.config && gameState.config.gameClass))) || 'unknown';
  //         // GA event: ensure we record game end on cleanup
  //         analytics.event('game_end', { /*action: "Ended " + gameName, category: 'game',*/ game_name: gameName, /*room_hash: roomHash,*/ player_count: playerCount, duration_seconds: dur / 1000 });
  //         playStartRef.current = null;
  //       }
  //     } catch (e) {}
  //   };
  // }, [gameState, gamePlayers, roomId]);

  // submitAction: attach caller metadata and emit to server using socket
  const submitAction = useCallback((action) => {
    if (!socket) return Promise.resolve({ ok: false, error: 'no-socket' });
    return new Promise((resolve) => {
      let responded = false;
      try {
        const payload = { roomId, action };
        socket.emit('gameAction', payload, (ack) => {
          responded = true;
          // If server explicitly returns an error, normalize
          if (ack && ack.error) return resolve({ ok: false, error: ack.error });
          // Server ack supplied - return it exactly (server determines status)
          // Ensure we do not mark server acks as optimistic
          if (ack && typeof ack === 'object') {
            // remove any optimistic flag if present from earlier
            if (ack.optimistic) delete ack.optimistic;
          }
          return resolve(ack);
        });
        // If server doesn't ack, resolve after a short timeout optimistically
        setTimeout(() => {
          if (!responded) resolve({ ok: true, optimistic: true });
        }, 1000);
      } catch (e) {
        resolve({ ok: false, error: String(e) });
      }
    });
  }, [socket, roomId]);

  return {
    focusRef,
    findPlayerById,
    getCurrentPlayer,
    isLocalPlayerTurn,
    submitAction,
    normalizedState,
    perspectiveUser,
    perspectiveIds,
  };
}
